import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Megaphone, ExternalLink } from 'lucide-react';

const ICON_MAP = { 'external-link': ExternalLink };
const ROTATE_MS = 10000;

const CampaignSlot = () => {
  const { i18n } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [langues, setLangues]     = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(false);
  const [index, setIndex]         = useState(0);
  const [paused, setPaused]       = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`/infofluo/communication.json?v=${Date.now()}`).then(r => { if (!r.ok) throw r; return r.json(); }),
      fetch(`/infofluo/communication_langues.json?v=${Date.now()}`).then(r => { if (!r.ok) throw r; return r.json(); }),
    ])
      .then(([c, l]) => {
        if (cancelled) return;
        console.log('communication data:', c);
        setCampaigns(c);
        setLangues(l);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) { setLoading(false); setError(true); }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (paused || campaigns.length <= 1) return;
    const t = setInterval(() => setIndex(i => (i + 1) % campaigns.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, campaigns.length]);

  if (loading) {
    return <div className="aspect-square rounded-2xl bg-white/10 animate-pulse" />;
  }

  if (error || campaigns.length === 0) return null;

  const lang    = (i18n.language || 'fr').split('-')[0];
  const tr      = langues[lang] || langues['fr'] || {};
  const current = campaigns[index] || campaigns[0];
  const Icon    = ICON_MAP[current.icon] || ExternalLink;

  const prev = () => setIndex(i => (i - 1 + campaigns.length) % campaigns.length);
  const next = () => setIndex(i => (i + 1) % campaigns.length);

  console.log('image src:', current.image);

  const Inner = (
    <div
      className="relative aspect-square rounded-2xl overflow-hidden bg-white shadow-2xl border border-white/15 group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <img
        src={current.image}
        alt={tr[current.alt_key] || ''}
        title={tr[current.title_key] || ''}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
        loading="lazy"
      />

      {/* Badge titre — top-left */}
      {tr[current.title_key] && (
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ter-blue text-white text-[11px] font-bold uppercase tracking-wider shadow-md max-w-[calc(100%-1.5rem)]">
          <Megaphone className="w-3 h-3 shrink-0" />
          <span className="truncate">{tr[current.title_key]}</span>
        </div>
      )}

      {/* CTA button — bottom-center, visible au survol */}
      {tr[current.button_key] && (
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 text-ter-blue text-[11px] font-semibold shadow-md">
            <Icon className="w-3 h-3" />
            {tr[current.button_key]}
          </span>
        </div>
      )}

      {/* Contrôles carrousel */}
      {campaigns.length > 1 && (
        <>
          <button
            onClick={e => { e.preventDefault(); prev(); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-ter-blue shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Précédent"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={e => { e.preventDefault(); next(); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-ter-blue shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Suivant"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {campaigns.map((c, i) => (
              <button
                key={c.id}
                onClick={e => { e.preventDefault(); setIndex(i); }}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-6 bg-ter-blue' : 'w-1.5 bg-white/80 hover:bg-white'}`}
                aria-label={`Campagne ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );

  return current.url ? (
    <a href={current.url} target={current.target || '_blank'} rel="noopener noreferrer" className="block">
      {Inner}
    </a>
  ) : Inner;
};

export default CampaignSlot;
